import React, { useState, useEffect } from 'react';
import { getPublicTeam } from '../services/api';

const DynamicTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [sectionContent, setSectionContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const response = await getPublicTeam();
      console.log('Team API Response:', response);
      
      // Extract team members data
      let teamData = [];
      if (response && response.data && Array.isArray(response.data)) {
        teamData = response.data;
      } else if (response && Array.isArray(response)) {
        teamData = response;
      } else if (response && response.team && Array.isArray(response.team)) {
        teamData = response.team;
      }
      
      // Filter active team members and sort by order
      const activeTeam = teamData
        .filter(member => member.isActive !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setTeamMembers(activeTeam);
      console.log('Team members loaded:', activeTeam.length);
      
      // Also fetch section content for title/subtitle
      try {
        const sectionResponse = await fetch('https://inteliheads.in/api/cms/public/content/team');
        const sectionData = await sectionResponse.json();
        if (sectionData && sectionData.data) {
          setSectionContent(sectionData.data);
        } else if (sectionData && sectionData._id) {
          setSectionContent(sectionData);
        }
      } catch (err) {
        console.log('No section content found, using defaults');
      }
      
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const getDefaultIcon = (department) => {
    const icons = {
      'Executive': '👔',
      'Engineering': '👨‍💻',
      'Sales': '📈',
      'Support': '🎧',
      'Marketing': '📢',
      'Leadership': '👑',
      'Technical': '🔧'
    };
    return icons[department] || '👤';
  };

  if (loading) {
    return (
      <section style={{ padding: '5rem 0', textAlign: 'center' }}>
        <div className="container">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
          <p style={{ marginTop: '1rem', color: '#5A6E7F' }}>Loading team members...</p>
        </div>
      </section>
    );
  }

  const title = sectionContent?.title || 'Leadership Team';
  const subtitle = sectionContent?.subtitle || 'Meet the experts driving innovation and excellence.';
  const backgroundColor = sectionContent?.backgroundColor || '#FFFFFF';
  const description = sectionContent?.content || '';

  return (
    <section id="team" style={{
      padding: 'clamp(2rem, 6vw, 5rem) 0',
      background: backgroundColor
    }}>
      <div className="container">
        <h2 style={{
          fontSize: 'clamp(1.6rem, 6vw, 2.25rem)',
          marginBottom: '1rem',
          color: '#0A192F',
          textAlign: 'center',
          fontWeight: 700
        }}>
          {title}
        </h2>
        <div style={{
          fontSize: 'clamp(0.9rem, 4vw, 1.125rem)',
          color: '#5A6E7F',
          textAlign: 'center',
          marginBottom: '1rem',
          maxWidth: '700px',
          margin: '0 auto 1rem'
        }}>
          {subtitle}
        </div>
        
        {description && (
          <div style={{
            fontSize: '1rem',
            color: '#5A6E7F',
            textAlign: 'center',
            marginBottom: '3rem',
            maxWidth: '800px',
            margin: '0 auto 3rem',
            lineHeight: '1.6'
          }}>
            {description}
          </div>
        )}

        {teamMembers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', background: '#F7F9FC', borderRadius: '20px' }}>
            <i className="fas fa-users" style={{ fontSize: '3rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
            <p style={{ color: '#5A6E7F' }}>No team members available at the moment.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'clamp(1.5rem, 4vw, 2rem)'
          }}>
            {teamMembers.map((member, index) => (
              <div
                key={member._id || index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                style={{
                  background: 'white',
                  padding: 'clamp(1.5rem, 5vw, 2rem)',
                  borderRadius: '24px',
                  textAlign: 'center',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
                  border: '1px solid #E2E8F0',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 20px 25px -12px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = '#2C7A7B';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                }}
              >
                {/* Profile Image / Avatar */}
                {member.photo ? (
                  <div style={{
                    width: '130px',
                    height: '130px',
                    margin: '0 auto 1rem',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img 
                      src={getFullImageUrl(member.photo)} 
                      alt={member.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div style="width: 130px; height: 130px; background: linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 3rem; color: white;">
                            ${getInitials(member.name)}
                          </div>
                        `;
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: '130px',
                    height: '130px',
                    background: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '3rem',
                    color: 'white'
                  }}>
                    {member.icon || getDefaultIcon(member.department) || getInitials(member.name)}
                  </div>
                )}

                {/* Department Badge */}
                {member.department && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <span style={{
                      background: '#E3F2FD',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      color: '#2C7A7B',
                      fontWeight: 600,
                      display: 'inline-block'
                    }}>
                      {member.department}
                    </span>
                  </div>
                )}

                {/* Name */}
                <h4 style={{
                  fontSize: '1.2rem',
                  marginBottom: '0.3rem',
                  color: '#0A192F',
                  fontWeight: 700
                }}>
                  {member.name}
                </h4>

                {/* Position/Title */}
                <div style={{
                  color: '#2C7A7B',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  {member.position}
                </div>

                {/* Bio */}
                {member.bio && (
                  <p style={{
                    fontSize: '0.85rem',
                    color: '#5A6E7F',
                    lineHeight: '1.5',
                    marginBottom: '1rem',
                    flex: 1
                  }}>
                    {member.bio.length > 100 ? `${member.bio.substring(0, 100)}...` : member.bio}
                  </p>
                )}

                {/* Social Links */}
                {(member.email || member.linkedin || member.twitter) && (
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center',
                    marginTop: '0.5rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #E2E8F0'
                  }}>
                    {member.email && (
                      <a 
                        href={`mailto:${member.email}`} 
                        style={{ color: '#5A6E7F', fontSize: '1.1rem', transition: 'color 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#2C7A7B'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#5A6E7F'}
                      >
                        <i className="fas fa-envelope"></i>
                      </a>
                    )}
                    {member.linkedin && (
                      <a 
                        href={member.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#5A6E7F', fontSize: '1.1rem', transition: 'color 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#2C7A7B'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#5A6E7F'}
                      >
                        <i className="fab fa-linkedin"></i>
                      </a>
                    )}
                    {member.twitter && (
                      <a 
                        href={member.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#5A6E7F', fontSize: '1.1rem', transition: 'color 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#2C7A7B'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#5A6E7F'}
                      >
                        <i className="fab fa-twitter"></i>
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default DynamicTeam;